import { createContext, PropsWithChildren, useMemo, useState } from 'react'
import { useUsermaven } from '@usermaven/react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutateFunction,
} from '@tanstack/react-query'
import { DAIRequest } from '../utils/DAIRequest'
import { useNotifications } from './notificationContext'
import {
  accountInfo,
  accountLink,
  AppointmentSettingsForm,
  Calendar,
} from 'src/pages/Settings/SettingsTypes'
import { AxiosResponse } from 'axios'

type UnipileContextType = {
  isLoading: boolean
  selectedCalendarData: AppointmentSettingsForm | undefined
  getAccountLink: UseMutateFunction<accountLink, Error, void, unknown>
  changeAccount: UseMutateFunction<any, Error, void, unknown>
  calendars: Calendar[] | undefined
  setCalendar: UseMutateFunction<any, Error, AppointmentSettingsForm, unknown>
  removeAccount: UseMutateFunction<
    AxiosResponse<any, any>,
    Error,
    void,
    unknown
  >
  accountInfo: accountInfo | undefined
  isRedirecting: boolean
}

const UnipileContext = createContext<UnipileContextType | undefined>(undefined)

export const UnipileContextProvider = ({ children }: PropsWithChildren) => {
  const { track } = useUsermaven()
  const { addNotification } = useNotifications()
  const queryClient = useQueryClient()
  const [selectedCalendarData, setSelectedCalendarData] =
    useState<AppointmentSettingsForm>()
  const [isRedirecting, setIsRedirecting] = useState(false)

  const { mutate: getAccountLink, isPending: isGenerateAccountLinkPending } =
    useMutation<accountLink>({
      mutationFn: async () => {
        return (await DAIRequest('GET', '/api/v1/unipile/generateAccountLink'))
          .data
      },
      onSuccess: async data => {
        await track('Generate_Account_Link_Success')
        setIsRedirecting(true)
        window.location.href = data.url
      },
      onError: async error => {
        await track('Generate_Account_Link_Failure', { message: error.message })
        addNotification('error', 'Error: ' + error.message)
      },
    })

  const { mutate: changeAccount, isPending: isChangeAccountPending } =
    useMutation({
      mutationFn: async () => {
        return (await DAIRequest('POST', '/api/v1/unipile/changeAccount')).data
      },
      onSuccess: async data => {
        await track('Change_Account_Success')
        setIsRedirecting(true)
        window.location.href = data.url
      },
      onError: async error => {
        await track('Change_Account_Failure', { message: error.message })
        addNotification('error', 'Error: ' + error.message)
      },
    })

  const { mutate: setCalendar, isPending: isSetCalendarPending } = useMutation({
    mutationFn: async (data: AppointmentSettingsForm) => {
      return (
        await DAIRequest('POST', '/api/v1/unipile/setCalendar', { body: data })
      ).data
    },
    onSuccess: async () => {
      await track('Set_Calendar_Success')
      addNotification('success', 'Calendar set.')
      await queryClient.invalidateQueries({ queryKey: ['accountInfo'] })
      await queryClient.invalidateQueries({ queryKey: ['calendars'] })
    },
    onError: async error => {
      await track('Set_Calendar_Failure', { message: error.message })
      addNotification('error', 'Error: ' + error.message)
    },
  })

  const { data: accountInfo, isLoading: isGetAccountLoading } =
    useQuery<accountInfo>({
      queryKey: ['accountInfo'],
      queryFn: async () => {
        const res = (await DAIRequest('GET', '/api/v1/unipile/getAccountInfo'))
          .data
        setSelectedCalendarData(res.calendarData)
        return res
      },
    })

  const { data: calendars, isLoading: isGetCalendarsLoading } = useQuery<
    Calendar[]
  >({
    queryKey: ['calendarEvents'],
    queryFn: async () => {
      return (
        await DAIRequest('GET', '/api/v1/unipile/getCalendars', {
          params: {
            to: new Date().toISOString(),
            from: new Date(new Date().getDay() + 7).toISOString(),
          },
        })
      ).data
    },
    enabled: accountInfo == undefined ? false : accountInfo?.isAuthorized,
  })

  const { mutate: removeAccount, isPending: isRemoveAccountPending } =
    useMutation({
      mutationFn: async () => {
        return await DAIRequest('POST', '/api/v1/unipile/removeAccount')
      },
      onSuccess: async () => {
        await track('Remove_Account_Success')
        addNotification('success', 'Email provider disconnected.')
        await queryClient.invalidateQueries({ queryKey: ['accountInfo'] })
        await queryClient.invalidateQueries({ queryKey: ['calendars'] })
      },
      onError: async error => {
        await track('Remove_Account_Failure', { message: error.message })
        addNotification('error', 'Error: ' + error.message)
      },
    })

  const isLoading = useMemo(
    () =>
      isGetAccountLoading ||
      isGetCalendarsLoading ||
      isRemoveAccountPending ||
      isChangeAccountPending ||
      isGenerateAccountLinkPending ||
      isSetCalendarPending,
    [
      isGetAccountLoading,
      isGetCalendarsLoading,
      isRemoveAccountPending,
      isChangeAccountPending,
      isGenerateAccountLinkPending,
      isSetCalendarPending,
    ]
  )

  return (
    <UnipileContext.Provider
      value={{
        isLoading,
        getAccountLink,
        calendars,
        changeAccount,
        selectedCalendarData,
        setCalendar,
        removeAccount,
        accountInfo,
        isRedirecting,
      }}
    >
      {children}
    </UnipileContext.Provider>
  )
}

export default UnipileContext
